import { formatCompactCatalogDate, formatPrice } from "./catalog";
import { comparisonOfferKey, comparisonPreviewProducts, comparisonUnitDifference,
  normalizeComparisonText, type ComparisonStarterFamily } from "./comparison-starter-shelf";
import { t, offerWord } from "./i18n";
import { formatWeightOrVolume, localized, type AppLocale, type Localized } from "./locale";
import type { ProductView, UnitPriceBasis } from "./types";

export const COMPARISON_PAGE_SIZE = 8;
export type ComparisonShelfMode = "home" | "search";
export interface ComparisonShelfView {
  families: readonly ComparisonStarterFamily[];
  totalFamilies: number;
  activeId: string | null;
  all: boolean;
  query: string;
  limit: number;
  mode: ComparisonShelfMode;
  locale: AppLocale;
  assetUrl: (path: string) => string;
}
function escape(value: string): string {
  return value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}
const chevron = '<svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true"><path d="m5 7.5 5 5 5-5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>';
const basisLabels: Record<UnitPriceBasis, Localized<string>> = {
  kg: { ru: "кг", ro: "kg", uk: "кг" }, l: { ru: "л", ro: "l", uk: "л" },
  "10pcs": { ru: "10 шт.", ro: "10 buc.", uk: "10 шт." },
  "100pcs": { ru: "100 шт.", ro: "100 buc.", uk: "100 шт." },
  "100sheets": { ru: "100 листов", ro: "100 foi", uk: "100 аркушів" },
};
const basisLabel = (basis: UnitPriceBasis, locale: AppLocale) => localized(locale, basisLabels[basis]);
export const comparisonFamilyLabel = (family: ComparisonStarterFamily, locale: AppLocale): string =>
  localized(locale, { ru: family.labelRu, ro: family.labelRo, uk: family.labelUk });
const variantLabel = (family: ComparisonStarterFamily, locale: AppLocale): string =>
  localized(locale, family.variant);
const productTitle = (product: ProductView, locale: AppLocale): string =>
  localized(locale, {
    ru: product.product_ru || product.product_ro,
    ro: product.product_ro || product.product_ru,
    uk: product.product_uk || product.product_ru || product.product_ro,
  });

export function filteredComparisonFamilies(options: ComparisonShelfView): ComparisonStarterFamily[] {
  if (!options.query.trim()) return [...options.families];
  const words = normalizeComparisonText(options.query).trim().split(/\s+/);
  return options.families.filter((family) => {
    const text = normalizeComparisonText([
      family.labelRu, family.labelRo, family.labelUk, family.variant.ru, family.variant.ro,
      family.variant.uk,
      ...family.products.map((p) => `${p.product_ru} ${p.product_ro} ${p.product_uk} ${p.brand ?? ""}`),
    ].join(" "));
    return words.every((word) => text.includes(word));
  });
}

function packageLabel(product: ProductView, options: ComparisonShelfView): string {
  return product.weight_or_volume
    ? formatWeightOrVolume(product.weight_or_volume.replace(/(\d)(?=(?:kg|ml|gr?|l)\b)/gi, "$1 ").replace(/\bgr\b/gi, "g"), options.locale)
    : `1 ${basisLabel(product.unit_price!.basis, options.locale)}`;
}
function unitText(product: ProductView, options: ComparisonShelfView): string {
  return `${formatPrice(product.unit_price!.minimum_mdl, options.locale)}/${basisLabel(product.unit_price!.basis, options.locale)}`;
}
function differenceText(product: ProductView, family: ComparisonStarterFamily, options: ComparisonShelfView): string {
  const delta = comparisonUnitDifference(product, family);
  return delta === 0 ? t("starterShelfMinimumShort") : `+${formatPrice(delta, options.locale)}/${basisLabel(family.basis, options.locale)}`;
}

function tileMarkup(
  family: ComparisonStarterFamily,
  options: ComparisonShelfView,
  searchMode = false,
): string {
  const label = comparisonFamilyLabel(family, options.locale);
  const fullVariant = variantLabel(family, options.locale);
  const variant = family.variant.id === "standard" || normalizeComparisonText(label).includes(normalizeComparisonText(fullVariant)) ? "" : fullVariant;
  const expanded = options.activeId === family.id;
  return `<button class="comparison-starter-family${searchMode ? " comparison-search-family" : ""}" type="button"
      id="comparison-tile-${escape(family.id)}" data-comparison-starter-family="${escape(family.id)}"
      aria-expanded="${expanded}" aria-controls="comparison-detail-${escape(family.id)}"
      aria-label="${escape(t("starterShelfOpenAria", { family: `${label}${variant ? ` · ${variant}` : ""}` }))}">
    <span class="comparison-starter-family-heading"><strong>${escape(label)}</strong>
      ${variant ? `<small>${escape(variant)}</small>` : ""}</span>
    <span class="comparison-starter-previews">${comparisonPreviewProducts(family).map((product) => `
      <span class="comparison-starter-preview">
        <img src="${escape(options.assetUrl(product.image))}" alt="" loading="lazy" decoding="async" width="28" height="28" />
        <span class="comparison-starter-preview-copy">
          <span class="comparison-starter-preview-store">${escape(product.catalog.chain)} <small>· ${escape(packageLabel(product, options))}</small></span>
          <b>${escape(formatPrice(product.new_price_mdl!, options.locale))}</b>
        </span>
        <span class="comparison-starter-unit-line"><span class="comparison-starter-unit">${escape(unitText(product, options))}</span>
          <span class="comparison-starter-difference" title="${escape(differenceText(product, family, options))}" aria-label="${escape(differenceText(product, family, options))}">${escape(comparisonUnitDifference(product, family) === 0 ? t("starterShelfMinimumBadge") : `+${formatPrice(comparisonUnitDifference(product, family), options.locale).replace(/ lei$/, "")}`)}</span>
        </span>
      </span>`).join("")}</span>
    <span class="comparison-starter-family-footer"><span>${family.products.length} ${escape(offerWord(family.products.length))}</span>${chevron}</span>
  </button>`;
}

function detailMarkup(family: ComparisonStarterFamily, options: ComparisonShelfView): string {
  const label = comparisonFamilyLabel(family, options.locale);
  const unit = basisLabel(family.basis, options.locale);
  return `<section class="comparison-starter-detail" id="comparison-detail-${escape(family.id)}"
      aria-labelledby="comparison-tile-${escape(family.id)}">
    <header><h4>${escape(label)} <small>${escape(variantLabel(family, options.locale))}</small></h4>
      <p>${escape(t("starterShelfStoreCount", { count: family.storeCount }))} · ${family.products.length} ${escape(offerWord(family.products.length))}</p></header>
    <p class="comparison-starter-explanation">${escape(t("starterShelfDeltaHelp", { unit }))}</p>
    ${family.comparisonGroupId.startsWith("household_") ? `<p class="comparison-starter-explanation">${escape(t("starterShelfConcentrationHelp", { unit }))}</p>` : ""}
    <div class="comparison-starter-offers">${family.products.map((product) => {
      const delta = comparisonUnitDifference(product, family);
      const title = productTitle(product, options.locale);
      return `<button class="related-product-card comparison-starter-offer" type="button"
          data-comparison-starter-product-key="${escape(comparisonOfferKey(product))}"
          aria-label="${escape(t("openRelatedProduct", { title }))}">
        <span class="related-product-image"><img src="${escape(options.assetUrl(product.image))}" alt="" loading="lazy" decoding="async" /></span>
        <span class="related-product-copy"><strong>${escape(title)}</strong>
          <span class="related-product-meta"><b>${escape(product.catalog.chain)}</b><span>${escape(packageLabel(product, options))}</span>
            <span>${escape(t("starterShelfUntil", { date: formatCompactCatalogDate(product.catalog.valid_until) }))}</span></span>
          <span class="comparison-starter-offer-prices"><b>${escape(formatPrice(product.new_price_mdl!, options.locale))}</b>
            <span>${escape(unitText(product, options))}</span></span>
          <span class="comparison-starter-offer-difference${delta === 0 ? " is-minimum" : ""}">${escape(delta === 0 ? t("starterShelfLowest") : differenceText(product, family, options))}</span>
        </span>
      </button>`;
    }).join("")}</div>
    <button type="button" class="comparison-starter-action" data-comparison-starter-close="${escape(family.id)}">${escape(t("starterShelfClose"))} ${chevron}</button>
  </section>`;
}

export function comparisonShelfGridMarkup(options: ComparisonShelfView): string {
  const filtered = filteredComparisonFamilies(options);
  const limit = options.mode === "search"
    ? options.families.length
    : options.all ? options.limit : options.families.length >= 4 ? 4 : 2;
  const visible = filtered.slice(0, limit);
  const rows: string[] = [];
  const columns = options.mode === "search" ? 1 : 2;
  for (let index = 0; index < visible.length; index += columns) {
    const pair = visible.slice(index, index + columns);
    rows.push(pair.map((family) => tileMarkup(family, options, options.mode === "search")).join(""));
    // Full-width disclosure after the clicked tile's row; never a carousel or
    // a navigation event, and never interleaved into the ordinary product list.
    const active = pair.find((family) => family.id === options.activeId);
    if (active) rows.push(detailMarkup(active, options));
    for (const family of pair) if (family.id !== active?.id) rows.push(
      `<section id="comparison-detail-${escape(family.id)}" hidden></section>`,
    );
  }
  return `<div class="comparison-starter-grid${options.mode === "search" ? " comparison-search-grid" : ""}">${rows.join("")}</div>
    ${!visible.length ? `<p class="comparison-starter-empty" role="status">${escape(t("starterShelfNothing"))}</p>` : ""}
    ${options.all ? `<p class="comparison-starter-counter" aria-live="polite">${escape(t("starterShelfCounter", { shown: visible.length, total: filtered.length }))}</p>` : ""}
    ${options.all && visible.length < filtered.length ? '<div class="comparison-starter-load-sentinel" data-comparison-starter-sentinel aria-hidden="true"></div>' : ""}`;
}

export function comparisonShelfMarkup(options: ComparisonShelfView): string {
  const searchMode = options.mode === "search";
  return `<div class="comparison-starter-heading">
      <h3 id="comparison-starter-shelf-title">${escape(t(searchMode ? "starterShelfSearchTitle" : "starterShelfTitle"))}</h3>
      ${searchMode ? `<p>${escape(t(options.families.length > 1 ? "starterShelfSearchChoose" : "starterShelfSearchHelp"))}</p>` : ""}
    </div>
    ${!searchMode ? `<div class="comparison-starter-search"><input id="comparison-starter-search" type="search" maxlength="100" autocomplete="off" aria-label="${escape(t("starterShelfSearch"))}" placeholder="${escape(t("starterShelfSearchPlaceholder"))}" value="${escape(options.query)}" />
        <button type="button" data-comparison-starter-clear aria-label="${escape(t("clearSearchAria"))}"${options.query ? "" : " hidden"}>×</button></div>` : ""}
    <div data-comparison-starter-grid>${comparisonShelfGridMarkup(options)}</div>
    ${searchMode
      ? `<button class="comparison-starter-action comparison-starter-all comparison-search-all" type="button" data-comparison-search-all aria-expanded="false">
        ${escape(t("starterShelfAll", { count: options.totalFamilies }))}${chevron}
      </button>`
      : options.all
      ? `<button class="comparison-starter-action comparison-starter-collapse-fab" type="button" data-comparison-starter-collapse>${escape(t("starterShelfCompact"))}${chevron}</button>`
      : `<button class="comparison-starter-action comparison-starter-all" type="button" data-comparison-starter-all aria-expanded="false">
        ${escape(t("starterShelfAll", { count: options.totalFamilies }))}${chevron}
      </button>`}`;
}
